import {
    ButtonBase,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import { useState } from "react";
import { useNavigate } from "react-router";
import { routeMapping } from "../../route.config";
import { IWithClassName, IWithThemeProps } from "../../theme/default.theme";

const tools = () => [
    {
        name: "List Builder",
        path: routeMapping.listBuilder.path,
        icon: <img src="/assets/list.svg" alt="List Builder Icon" />,
    },
    {
        name: "Data Browser",
        path: routeMapping.dataBrowser.path,
        icon: <img src="/assets/data.svg" alt="Data Browser Icon" />,
    },
    {
        name: "User Preferences",
        path: routeMapping.userPreferences.path,
        icon: <img src="/assets/gear.svg" alt="User Preferences Icon" />,
    },
    {
        name: "Tracked Lists",
        path: routeMapping.trackedLists.path,
        icon: <img src="/assets/star.png" alt="Tracked Lists Icon" />,
    },
    {
        name: "Change Log",
        path: routeMapping.changeLog.path,
        icon: <img src="/assets/change.svg" alt="Change Log Icon" />,
    },
    {
        name: "FAQ",
        path: routeMapping.faq.path,
        icon: <img src="/assets/faq.svg" alt="Faq Icon" />,
    },
];

const Sidebar = ({ className }: IWithClassName) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const navigate = useNavigate();

    const handleClick = (path: string) => {
        void navigate(path);
    };

    const handleExpandClick = () => setIsExpanded(!isExpanded);

    const handleLogoClick = () => {
        void navigate("/");
    };

    return (
        <div className={clsx(className, { expanded: isExpanded })}>
            <div className="center-icon" onClick={handleExpandClick}>
                <img
                    src={
                        isExpanded
                            ? "/assets/minus-icon.svg"
                            : "/assets/plus-icon.svg"
                    }
                    className="toggle-icon"
                    alt={isExpanded ? "minus" : "plus"}
                />
            </div>
            <div className="content-wrapper">
                <div className="logo-container">
                    {isExpanded ? (
                        <img
                            src="/assets/logo-expanded.png"
                            className="logo-expanded"
                            alt="Logo Expanded"
                            onClick={() => void handleLogoClick()}
                        />
                    ) : (
                        <img
                            src="/assets/logo-collapsed.png"
                            className="logo-collapsed"
                            alt="Logo Collapsed"
                            onClick={() => void handleLogoClick()}
                        />
                    )}
                </div>
                <List component="nav">
                    {tools().map((tool) => (
                        <ListItem key={tool.name}>
                            <ButtonBase onClick={() => handleClick(tool.path)}>
                                <ListItemIcon>{tool.icon}</ListItemIcon>
                                <ListItemText
                                    className="tool-text"
                                    primary={tool.name}
                                />
                            </ButtonBase>
                        </ListItem>
                    ))}
                </List>
            </div>
        </div>
    );
};

export default styled(Sidebar)(({ theme }: IWithThemeProps) => ({
    position: "fixed",
    top: 0,
    left: 0,
    width: 124,
    height: "100%",
    backgroundColor: theme?.palette.sidebar.bgColor,
    flexGrow: 0,
    padding: theme?.spacing(2),
    boxSizing: "border-box",
    zIndex: 1001,
    transition: "0.2s",
    borderRight: `4px solid ${theme?.palette.sidebar?.inActiveLineColor ?? ""}`,

    "&.expanded": {
        width: 337,
        borderRight: `4px solid ${
            theme?.palette.sidebar?.activeLineColor ?? ""
        }`,
        ".tool-text": {
            visibility: "visible",
        },
    },

    ".content-wrapper": {
        overflow: "hidden",
        height: "100%",
    },

    ".logo-container": {
        height: 105,
    },

    ".logo-expanded": {
        padding: theme?.spacing(3),
        cursor: "pointer",
        zIndex: 1,
    },

    ".logo-collapsed": {
        padding: theme?.spacing(3),
        cursor: "pointer",
        zIndex: 1,
    },

    ".center-icon": {
        position: "absolute",
        right: -23,
        top: 91,
        cursor: "pointer",
        zIndex: 1,
    },

    ".toggle-icon": {
        height: 42,
        width: 42,
    },

    ".MuiList-root": {
        paddingTop: theme?.spacing(10),
    },

    ".MuiListItem-root": {
        marginBottom: theme?.spacing(5),
        whiteSpace: "nowrap",
    },

    ".MuiListItemIcon-root": {
        marginRight: theme?.spacing(2),
        marginLeft: theme?.spacing(2),
    },

    ".MuiListItemText-root": {
        fontSize: 18,
        color: theme?.palette.sidebar.textColor,
    },

    ".MuiListItemText-root .MuiTypography-root": {
        fontWeight: 700,
    },

    ".tool-text": {
        visibility: "collapse",
    },
}));
